import React, { useMemo, FC, ReactElement, useEffect } from 'react'
import { ThemeProvider, StyledEngineProvider, createTheme, responsiveFontSizes } from '@mui/material/styles'
import { SxProps } from '@mui/system'
import CssBaseline from '@mui/material/CssBaseline'
import Box from '@mui/material/Box'

import {
  TEXTFIELD_VARIANT_STANDARD,
  BUTTON_VARIANT_OUTLINED,
  PAPER_VARIANT_OUTLINED,
} from '@kaidam/fc-shared/src/constants/mui'

import {
  TOPBAR_HEIGHT,
  APP_LOGO_HEARTS,
  APP_LOGO_TEXT,
  APP_HEADER,
  APP_ARIA_LOGO,
  APP_ARIA_TEXT,
  APP_FF_LOGO_TEXT,
  APP_FF_LOGO,
  APP_FF_HEADER,
} from './settings'

export interface IThemeConfig {
  primary: string,
  secondary: string,
  background: string,
  getLogo: {
    (): ReactElement,
  },
  getToolbarSx: {
    (): SxProps,
  }
}

export const THEME_CONFIGS: Record<string, IThemeConfig> = {
  franchisecloud: {
    primary: '#6991F2',
    secondary: '#AF56C2',
    background: '#FAFBFF',
    getLogo: () => (
      <>
        <Box
          component='img'
          src={ APP_LOGO_HEARTS }
          sx={{
            maxHeight: '18px',
            display: 'block',
            ml: 0,
            my: 'auto',
          }}
        />
        <Box
          component='img'
          src={ APP_LOGO_TEXT }
          sx={{
            maxHeight: '16px',
            display: 'block',
            ml: 2,
            my: 'auto',
          }}
        />
      </>
    ),
    getToolbarSx: () => ({
      backgroundColor: '#fff',
      backgroundImage: `url("${APP_HEADER}")`,
      backgroundSize: `auto ${TOPBAR_HEIGHT}px`,
      backgroundRepeat: 'no-repeat',
      borderRadius: 0,
      borderImage: 'linear-gradient(90deg, #B055C1, #5D9BFB 20%) 1',
      borderWidth: '0 0 1px 0',
      borderStyle: 'solid',
    }),
  },
  aria: {
    primary: '#057DFC',
    secondary: '#01D2FE',
    background: '#FAFBFF',
    getLogo: () => (
      <>
        <Box
          component='img'
          src={ APP_ARIA_LOGO }
          sx={{
            maxHeight: '38px',
            display: 'block',
            ml: 0,
            mr: 2,
            my: 'auto',
          }}
        />
        <Box
          component='img'
          src={ APP_ARIA_TEXT }
          sx={{
            maxHeight: '24px',
            display: 'block',
            ml: 0,
            my: 'auto',
          }}
        />
      </>
    ),
    getToolbarSx: () => ({
      backgroundColor: '#fff',
      backgroundRepeat: 'no-repeat',
      borderBottom: '1px solid #E5E5E5',
      borderRadius: 0,
    }),
  },
  franchisefunnels: {
    primary: '#555555',
    secondary: '#888888',
    background: '#FFFFFF',
    getLogo: () => (
      <>
        <Box
          component='img'
          src={ APP_FF_LOGO }
          sx={{
            maxHeight: '38px',
            display: 'block',
            ml: 0,
            mr: 0,
            my: 'auto',
          }}
        />
        <Box
          component='img'
          src={ APP_FF_LOGO_TEXT }
          sx={{
            maxHeight: '32px',
            display: 'block',
            ml: 0,
            my: 'auto',
          }}
        />
      </>
    ),
    getToolbarSx: () => ({
      backgroundColor: '#FFFFFF',
      backgroundImage: `url("${APP_FF_HEADER}")`,
      backgroundSize: `auto ${TOPBAR_HEIGHT}px`,
      backgroundRepeat: 'no-repeat',
      borderRadius: 0,
      borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    }),
  }
}

export const THEME_DOMAINS: Record<string, string> = {
  'app.franchisecloud.io': 'franchisecloud',
  'app.franchisemanager.ai': 'aria',
  'app.getaria.ai': 'aria',
  'app.franchisefunnels.io': 'franchisefunnels',
}

export const getThemeConfig = (): IThemeConfig => {
  if (typeof document !== "undefined") {
    const params = new URLSearchParams(new URL(document.URL).search);
    const queryValue = params.get('theme_config');
    if(queryValue) {
      localStorage.setItem('theme_config', queryValue)
    }
  }
  const localStorageValue = localStorage.getItem('theme_config')
  if(localStorageValue) {
    if(THEME_CONFIGS[localStorageValue]) {
      return THEME_CONFIGS[localStorageValue]
    }
    else {
      localStorage.removeItem('theme_config')
    }
  }
  if (typeof document !== "undefined") {
    const domainName = THEME_DOMAINS[document.location.hostname]
    if(domainName && THEME_CONFIGS[domainName]) return THEME_CONFIGS[domainName]
  }
  return THEME_CONFIGS.franchisecloud
}

const AppTheme: FC<React.PropsWithChildren<unknown>> = (props) => {

  const themeConfig = getThemeConfig()

  const palette = {
    primary: {
      main: themeConfig.primary,
    },
    secondary: {
      main: themeConfig.secondary,
    },
    background: {
      default: themeConfig.background,
    },
  }

  const globalComponents = {
    components: {
      MuiTypography: {
        styleOverrides: {
          root: {
            fontFamily: '"Nunito Sans", sans-serif, Arial, Helvetica',
          },
          h1: {
            fontSize: '2.6rem',
            fontWeight: 800,
            lineHeight: '100%',
          },
          h2: {
            fontSize: '1.8rem',
            fontWeight: 700,
            lineHeight: '110%',
          },
          h3: {
            fontSize: '1.4rem',
            fontWeight: 600,
            lineHeight: '120%',
          },
          h4: {
            fontSize: '1.2rem',
            fontWeight: 600,
            lineHeight: '130%',
          },
          h5: {
            fontSize: '1rem',
            fontWeight: 500,
            lineHeight: '140%',
          },
          h6: {
            fontSize: '0.9rem',
            fontWeight: 500,
            lineHeight: '150%',
          },
          subtitle1: {
            fontSize: '1rem',
            fontWeight: 300,
            lineHeight: '160%',
          },
          subtitle2: {
            fontSize: '0.875rem',
            fontWeight: 500,
            lineHeight: '160%',
          },
          body1: {
            fontSize: '1rem',
            fontWeight: 500,
            color: '#202020',
            fontFamily: '"Open Sans", sans-serif, Arial, Helvetica',
            lineHeight: '140%',
          },
          body2: {
            fontSize: '0.8rem',
            fontWeight: 300,
            color: '#202020',
            fontFamily: '"Open Sans", sans-serif, Arial, Helvetica',
            lineHeight: '140%',
          },
        },
      },
      MuiTooltip: {
        defaultProps: {
          placement: 'top' as 'top'
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontFamily: '"Open Sans", sans-serif, Arial, Helvetica',
            minHeight: '40px',
            fontSize: '0.7rem',
            fontWeight: 500,
            padding: '6px 18px',
            borderRadius: '8px',
            boxShadow: 'none',
            '&.Mui-selected': {
              boxShadow: 'none',
            },
            '&.hover': {
              boxShadow: 'none',
            },
          },
        },
        defaultProps: {
          disableElevation: true,
          selected: false,
          variant: BUTTON_VARIANT_OUTLINED,
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            mb: 2,
            p:1.5,
          },
        },
        defaultProps: {
          variant: TEXTFIELD_VARIANT_STANDARD,
          fullWidth: true,
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
            //border: 'none',
          },
        },
        defaultProps: {
          variant: PAPER_VARIANT_OUTLINED,
          elevation: 0,
        },
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            maxHeight: `${TOPBAR_HEIGHT}px`,
            '@media (min-width: 600px)': {
              minHeight: `${TOPBAR_HEIGHT}px`,
            }
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            height: 5,
          },
          head:{
            height: 5,
          },
        },
      },
    },
  }

  // Function to set CSS custom properties
  const setCSSCustomProperties = (theme: any) => {
    document.documentElement.style.setProperty('--theme-primary-color', theme.palette.primary.main)
    document.documentElement.style.setProperty('--theme-secondary-color', theme.palette.secondary.main)
    document.documentElement.style.setProperty('--theme-primary-color-dark', theme.palette.primary.dark)
    document.documentElement.style.setProperty('--theme-secondary-color-dark', theme.palette.secondary.dark)
  }

  let theme = useMemo(() => {
    return createTheme({
      palette,
      ...globalComponents,
    })
  }, [
    palette
  ])

  theme = responsiveFontSizes(theme)

  // Use effect to set CSS custom properties when the theme changes
  useEffect(() => {
    setCSSCustomProperties(theme)
  }, [theme])
  
  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={ theme }>
        <CssBaseline />
        { props.children }
      </ThemeProvider>
    </StyledEngineProvider>
  )
}

export default AppTheme